// @flow
// $FlowFixMe
import React, { useMemo, Fragment } from "react";
import { get, noop } from "lodash";
import type { BinaryFn } from "@tvg/types/Functional";
import type {
  Results,
  RaceTypeCodeEnum,
  RaceProgram,
  Payoffs
} from "@tvg/types/Race";
import type { RaceBet } from "@fdr/types/MyBets";
import buildRaceUrl from "@tvg/formatter/url";
import { useNextRaceUrl } from "@fdr/custom-hooks/ProgramPage";
import RacecardMask from "@fdr/static-ui/Masks/racecard";
import Button from "../../_atoms/Button";
import { InlineMessage } from "../../_molecules/MessageV2";
import RaceRunnersPayoffs from "../../_organisms/RaceRunnersPayoffs";
import RacePayoffs from "../../_organisms/RacePayoffs";
import PendingRunnerPayoffs from "../../_organisms/PendingRunnerPayoffs";
import RaceResultsHeader from "../../_molecules/RaceResultsHeader";
import { InlineMessageButtons } from "./styled-components";

type Props = {
  /**
   * Object with the results and payoffs for the race
   */
  results: ?Results,
  /**
   Array with the list of scratched runners for the full/basic result layout
   */
  scratchedRunners: string[],
  /**
   * Indicates if the results are still pending
   */
  isResultsPending: boolean,
  /**
   * Array with the bets placed on the race
   */
  bets: Array<RaceBet>,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * race
   */
  race: RaceProgram,
  /**
   * races
   */
  races: RaceProgram[],
  changeRace: BinaryFn<string, string, void>,
  isLoading: boolean,
  showSilks: boolean
};

const RaceOfficials = (props: Props) => {
  const {
    isResultsPending,
    results,
    raceTypeCode,
    race,
    races,
    changeRace,
    isLoading,
    showSilks
  } = props;

  const { nextRace, forwardDisabled } = useNextRaceUrl(races, race);

  const getPayoffs = () =>
    get(results, "payoffs", []).filter(
      (payoff) =>
        get(payoff, "wagerType.name") !== "Win" &&
        get(payoff, "wagerType.name") !== "Show" &&
        get(payoff, "wagerType.name") !== "Place"
    );

  const getWinPlaceShowPayoffs = () => {
    let payoffs = get(results, "payoffs", []).filter(
      (payoff) =>
        get(payoff, "wagerType.name") === "Win" ||
        get(payoff, "wagerType.name") === "Show" ||
        get(payoff, "wagerType.name") === "Place"
    );

    if (get(results, "runners.length") > 0) {
      const amount = get(results, "runners[0].betAmount");
      payoffs = payoffs.map((payoff: Payoffs) => ({
        ...payoff,
        wagerAmount: amount
      }));
    }

    return payoffs;
  };

  const renderResults = () => {
    if (get(results, "payoffs.length") > 0 && !isLoading) {
      return (
        <Fragment>
          <RaceRunnersPayoffs
            raceTypeCode={raceTypeCode}
            key="results-tab-runners"
            runners={get(results, "runners", [])}
            payoffs={getWinPlaceShowPayoffs()}
            showSilks={showSilks}
          />
          <RacePayoffs key="results-tab-payoffs" payoffs={getPayoffs()} />
        </Fragment>
      );
    }

    return isLoading ? (
      <RacecardMask />
    ) : (
      <Fragment>
        <RaceResultsHeader
          hasTopBorder
          hasWin
          hasPlace
          hasShow
          data-qa-label="raceResults-header"
        />
        <PendingRunnerPayoffs />
      </Fragment>
    );
  };

  const raceUrl = buildRaceUrl(
    get(race, "track.trackCode", ""),
    get(race, "track.trackName", ""),
    nextRace
  );

  return useMemo(
    () => (
      <section key="results-tab">
        {!isResultsPending ? (
          renderResults()
        ) : (
          <Fragment>
            <InlineMessage
              type="info"
              title="Awaiting Results"
              text="Replays and results can take up to 15 mins. Check back soon."
            >
              <InlineMessageButtons>
                <Button
                  isInternalLink
                  type="secondary"
                  size="big"
                  text="Change Track"
                  url="/tracks"
                  onClick={() => changeRace("Change Track", `/tracks`)}
                />
                {!forwardDisabled && (
                  <Button
                    isInternalLink
                    type="primary"
                    size="big"
                    text="Next Race"
                    url={raceUrl}
                    onClick={() => changeRace("Next Race", raceUrl)}
                  />
                )}
              </InlineMessageButtons>
            </InlineMessage>
            <RaceResultsHeader
              hasTopBorder
              hasWin
              hasPlace
              hasShow
              data-qa-label="raceResults-header"
            />
            <PendingRunnerPayoffs />
          </Fragment>
        )}
      </section>
    ),
    [
      isResultsPending,
      nextRace,
      isLoading,
      forwardDisabled,
      showSilks,
      JSON.stringify(results),
      JSON.stringify(race)
    ]
  );
};

RaceOfficials.defaultProps = {
  bettingInterests: [],
  scratchedRunners: [],
  results: null,
  raceTypeCode: "T",
  isResultsPending: false,
  bets: [],
  race: {},
  races: [],
  changeRace: noop,
  isLoading: true,
  showSilks: false
};

export default RaceOfficials;
